/**
 * googleRoad.ts — the road between stops, without a map on screen.
 *
 * Phones don't mount the split map, so a guest there never got Google's road
 * and "Find camps on this leg" fell back to a straight line with a wider
 * corridor: same leg, different count ("22 within 8 km of the road" on a
 * laptop, "26 within 20 km of the direct line" on a phone). This asks the
 * Directions service for the road directly, through the one shared loader.
 */

import { getGoogleMapsLoader } from '../lib/googleMapsLoader';
import type { LatLng } from './campsAlong';

export interface RoadRoute {
  /** The road per leg, thinned to ~600 points. */
  paths: LatLng[][];
  /** Google's figures per leg, rounded exactly as the map's route does. */
  legs: { distanceKm: number; driveMinutes: number }[];
}

const cache = new Map<string, Promise<RoadRoute | null>>();

const keyOf = (pts: LatLng[]) => pts.map((p) => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`).join('|');

/**
 * One road path per leg (stop i → stop i+1), or null when Directions can't
 * route it. Thinned to ~600 points a leg. Cached per stop list.
 */
export function fetchRoadLegs(stops: LatLng[], timeoutMs = 8000): Promise<LatLng[][] | null> {
  return fetchRoadRoute(stops, timeoutMs).then((r) => r?.paths ?? null);
}

/**
 * The road AND Google's per-leg km / minutes, for screens with no map (a
 * phone). The same request the desktop map makes, so a leg reads the same
 * on both ("241 km · 2h 45m", not 241 on a laptop and 191 on a phone).
 */
export function fetchRoadRoute(stops: LatLng[], timeoutMs = 8000): Promise<RoadRoute | null> {
  if (stops.length < 2 || stops.length > 25 || typeof window === 'undefined') return Promise.resolve(null);
  const key = keyOf(stops);
  const hit = cache.get(key);
  if (hit) return hit;
  const p = (async () => {
    try {
      const { DirectionsService, TravelMode } = (await getGoogleMapsLoader().importLibrary('routes')) as google.maps.RoutesLibrary;
      const svc = new DirectionsService();
      const [origin, ...rest] = stops;
      const destination = rest[rest.length - 1];
      const waypoints = rest.slice(0, -1).map((location) => ({ location, stopover: true }));
      const result = await Promise.race([
        svc.route({ origin, destination, waypoints, optimizeWaypoints: false, travelMode: TravelMode.DRIVING }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
      ]);
      const legs = result?.routes?.[0]?.legs;
      if (!legs || legs.length !== stops.length - 1) return null;
      return {
        paths: legs.map((leg) => {
          const pts: LatLng[] = [];
          (leg.steps ?? []).forEach((step) => (step.path ?? []).forEach((q) => pts.push({ lat: q.lat(), lng: q.lng() })));
          const step = Math.max(1, Math.ceil(pts.length / 600));
          const thin = pts.filter((_, i) => i % step === 0);
          if (pts.length && thin[thin.length - 1] !== pts[pts.length - 1]) thin.push(pts[pts.length - 1]);
          return thin;
        }),
        legs: legs.map((leg) => ({
          distanceKm: Math.round((leg.distance?.value ?? 0) / 1000),
          driveMinutes: Math.round((leg.duration?.value ?? 0) / 60),
        })),
      };
    } catch {
      return null;
    }
  })();
  cache.set(key, p);
  // A failure shouldn't stick: let the next open try again.
  void p.then((r) => {
    if (!r) cache.delete(key);
  });
  return p;
}
